import { useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography
} from "@mui/material"
import { toast } from "react-toastify"

import {
  IntegrationProductsDocument,
  useIntegrationProductSearchLazyQuery,
  useSetIntegrationProductMutation
} from "src/graphql/quark/quark.type"
import { readWholeNumber } from "../stockBuffer"

type Candidate = {
  productId: string
  name: string
  barcode?: string | null
  active: boolean
}

/**
 * Two explicit steps: pick the product, then type the SKU the integration
 * knows it by. Nothing is matched or suggested automatically. Stock buffer and
 * max sales quantity are optional and can be changed later.
 */
const AddProductModal = ({
  connectionId,
  integrationName,
  defaultStockBuffer,
  open,
  onClose
}: {
  connectionId: string
  integrationName: string
  defaultStockBuffer: number | null
  open: boolean
  onClose: () => void
}) => {
  const [term, setTerm] = useState("")
  const [selected, setSelected] = useState<Candidate | null>(null)
  const [sku, setSku] = useState("")
  const [buffer, setBuffer] = useState("")
  const [maxSales, setMaxSales] = useState("")

  const bufferField = readWholeNumber(buffer)
  const maxSalesField = readWholeNumber(maxSales, 1)

  const [search, { data, loading }] = useIntegrationProductSearchLazyQuery({
    fetchPolicy: "network-only"
  })

  const [setProduct, { loading: saving }] = useSetIntegrationProductMutation({
    refetchQueries: [
      { query: IntegrationProductsDocument, variables: { connectionId } }
    ]
  })

  const handleClose = () => {
    setTerm("")
    setSelected(null)
    setSku("")
    setBuffer("")
    setMaxSales("")
    onClose()
  }

  const handleSearch = () => {
    if (term.trim().length < 2) {
      toast.info("Type at least two characters to search.")
      return
    }
    void search({ variables: { query: term.trim() } })
  }

  const handleSave = async () => {
    if (!selected || !sku.trim() || bufferField.invalid || maxSalesField.invalid) return

    try {
      await setProduct({
        variables: {
          input: {
            connectionId,
            productId: selected.productId,
            externalSku: sku.trim(),
            stockBuffer: bufferField.value,
            maximumSalesQuantity: maxSalesField.value
          }
        }
      })
      toast.success(`${selected.name} will publish as ${sku.trim()}`)
      handleClose()
    } catch (error: any) {
      toast.error(error?.message ?? "Could not add the product")
    }
  }

  const results: Candidate[] = data?.fusionIntegrationProductSearch ?? []

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add product</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            label="Search by name or barcode"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button variant="outlined" onClick={handleSearch} disabled={loading}>
            Search
          </Button>
        </Box>

        {!selected && (
          <List dense sx={{ maxHeight: 280, overflow: "auto" }}>
            {results.map((product) => (
              <ListItemButton
                key={product.productId}
                onClick={() => setSelected(product)}
              >
                <ListItemText
                  primary={product.name}
                  secondary={[
                    product.barcode ? `Barcode ${product.barcode}` : null,
                    product.active ? null : "Inactive"
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </ListItemButton>
            ))}
            {!loading && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" p={2}>
                No products yet — search above.
              </Typography>
            )}
          </List>
        )}

        {selected && (
          <Box>
            <Typography variant="subtitle2">{selected.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.barcode ? `Barcode ${selected.barcode}` : "No barcode"}
            </Typography>
            {!selected.active && (
              <Typography variant="caption" color="warning.main" display="block">
                This product is inactive and will be published as unavailable.
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="SKU on this integration"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="Stock buffer (optional)"
              value={buffer}
              onChange={(e) => setBuffer(e.target.value)}
              inputProps={{ inputMode: "numeric" }}
              error={bufferField.invalid}
              helperText={
                bufferField.invalid
                  ? "Enter a whole number of units, or leave it empty."
                  : `Units kept off ${integrationName}. Empty uses ${
                      defaultStockBuffer != null ? `the integration's buffer (${defaultStockBuffer})` : "no buffer"
                    }.`
              }
            />
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="Max per order (optional)"
              value={maxSales}
              onChange={(e) => setMaxSales(e.target.value)}
              inputProps={{ inputMode: "numeric" }}
              error={maxSalesField.invalid}
              helperText={
                maxSalesField.invalid
                  ? "Enter a whole number from 1, or leave it empty."
                  : "Most units one order may take, never more than the stock on offer. Empty means no limit."
              }
            />
            <Button size="small" sx={{ mt: 1 }} onClick={() => setSelected(null)}>
              Choose a different product
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!selected || !sku.trim() || bufferField.invalid || maxSalesField.invalid || saving}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddProductModal
